import { alpha, type Theme } from '@mui/material';
import { MODULE_ACCENT_COLORS } from '../../../../../theme/module-colors';
import { MOTION_DURATION_MS, MOTION_EASING, motionTransition } from '../../../../../theme/motion';
import type { TemperatureCardMode } from '../utils/iot-suggestion';

const ACCENT = MODULE_ACCENT_COLORS.temperature.main;

type EquipmentCardVisualState =
  | 'pending'
  | 'in-progress'
  | 'flagged'
  | 'recorded-ok'
  | 'recorded-ko'
  | 'recorded-managed';

export function resolveEquipmentCardVisualState({
  cardMode,
  isUserConfirmed,
  isCompliant,
  isNcManaged = false,
}: {
  cardMode: TemperatureCardMode;
  isUserConfirmed: boolean;
  isCompliant: boolean | null;
  /** A signed corrective document closed today's non-conformity. */
  isNcManaged?: boolean;
}): EquipmentCardVisualState {
  if (cardMode === 'fulfilled') {
    if (isCompliant !== false) {
      return 'recorded-ok';
    }
    // Once the corrective document is signed the card goes back to plain
    // white: staying red would tell the operator the problem is still open.
    return isNcManaged ? 'recorded-managed' : 'recorded-ko';
  }
  if (cardMode === 'suggestion') {
    return 'flagged';
  }
  if (cardMode === 'surveilled') {
    return 'pending';
  }
  // 'due'
  if (isCompliant === false) {
    return 'flagged';
  }
  if (isUserConfirmed && isCompliant === true) {
    return 'in-progress';
  }
  return 'pending';
}

export function getCardStyles(theme: Theme, state: EquipmentCardVisualState) {
  const base = {
    position: 'relative' as const,
    borderRadius: 3,
    border: '1px solid',
    transition: motionTransition(
      ['border-color', 'box-shadow', 'background-color'],
      MOTION_DURATION_MS.routine,
      MOTION_EASING.standard,
    ),
    overflow: 'hidden' as const,
    bgcolor: 'background.paper',
  };

  switch (state) {
    case 'recorded-managed':
      return {
        ...base,
        borderColor: theme.palette.divider,
      };
    case 'recorded-ok':
      return {
        ...base,
        borderColor: alpha(theme.palette.success.main, 0.35),
        bgcolor: alpha(theme.palette.success.main, 0.04),
      };
    case 'recorded-ko':
      return {
        ...base,
        borderColor: alpha(theme.palette.error.main, 0.4),
        bgcolor: alpha(theme.palette.error.main, 0.04),
      };
    case 'in-progress':
      return {
        ...base,
        borderColor: alpha(ACCENT, 0.4),
        bgcolor: alpha(ACCENT, 0.04),
        boxShadow: `0 0 0 3px ${alpha(ACCENT, 0.08)}`,
      };
    case 'flagged':
      return {
        ...base,
        borderColor: alpha(theme.palette.error.main, 0.45),
        bgcolor: alpha(theme.palette.error.main, 0.04),
        boxShadow: `0 0 0 3px ${alpha(theme.palette.error.main, 0.08)}`,
      };
    default:
      return {
        ...base,
        borderColor: theme.palette.divider,
        '&:hover': {
          borderColor: alpha(ACCENT, 0.45),
          boxShadow: `0 4px 16px ${alpha(ACCENT, 0.1)}`,
        },
      };
  }
}

export function getAdjustButtonStyles(theme: Theme) {
  return {
    width: { xs: 56, sm: 48 },
    height: { xs: 56, sm: 48 },
    minWidth: { xs: 56, sm: 48 },
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 2,
    backgroundColor: 'background.paper',
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.08),
      borderColor: theme.palette.primary.main,
    },
    '&:disabled': {
      borderColor: theme.palette.divider,
      opacity: 0.5,
    },
  };
}

export function getTemperatureInputStyles() {
  return {
    flex: 1,
    '& .MuiOutlinedInput-root': {
      fontSize: { xs: '1rem', sm: '1rem' },
      minHeight: { xs: '56px', sm: '48px' },
      '& input': {
        padding: { xs: '16px 14px', sm: '14px' },
        fontSize: { xs: '1rem', sm: '1rem' },
        textAlign: 'center',
      },
    },
    '& .MuiInputLabel-root': {
      fontSize: { xs: '0.9375rem', sm: '1rem' },
    },
  };
}
